import { forwardRef, useCallback } from "react";
import GetAppIcon from "@mui/icons-material/GetApp";
import { defaultExporter } from "ra-core";
import {
  Button,
  fetchRelatedRecords,
  useDataProvider,
  useListContext,
  useNotify,
  type ExportButtonProps,
  type RaRecord,
} from "react-admin";
import { fetchAllListRows } from "@/utils/fetch-all-list-rows";
import { getListExportFilter } from "@/utils/list-export-filter";
import { recordsForCsvExport } from "@/utils/records-for-csv-export";

/**
 * Drop-in replacement for react-admin's ExportButton that pages through the full result set.
 * The API caps each list request at 500 rows; the stock button only fetches one page.
 */
export const ExportButton = forwardRef<HTMLButtonElement, ExportButtonProps>(
  function ExportButton(props, ref) {
    const {
      maxResults,
      onClick,
      label = "ra.action.export",
      icon = defaultIcon,
      exporter: customExporter,
      meta,
      ...rest
    } = props;

    const {
      filter,
      filterValues,
      resource,
      sort,
      exporter: exporterFromContext,
      total,
    } = useListContext();

    const exporter = customExporter || exporterFromContext;
    const dataProvider = useDataProvider();
    const notify = useNotify();

    const handleClick = useCallback(
      async (event: React.MouseEvent<HTMLButtonElement>) => {
        const listFilter = getListExportFilter(
          filterValues as Record<string, unknown> | undefined,
          filter as Record<string, unknown> | undefined
        );

        try {
          const data = await fetchAllListRows(dataProvider, resource, {
            sort,
            filter: listFilter,
            meta,
            maxResults,
          });

          if (exporter) {
            const exportData =
              exporter === defaultExporter ? recordsForCsvExport(data as RaRecord[]) : data;
            await exporter(exportData, fetchRelatedRecords(dataProvider), dataProvider, resource);
          }
        } catch (error) {
          console.error(error);
          notify("ra.notification.http_error", { type: "error" });
        }

        onClick?.(event);
      },
      [
        dataProvider,
        exporter,
        filter,
        filterValues,
        maxResults,
        meta,
        notify,
        onClick,
        resource,
        sort,
      ]
    );

    return (
      <Button ref={ref} onClick={handleClick} label={label} disabled={total === 0} {...rest}>
        {icon}
      </Button>
    );
  }
);

const defaultIcon = <GetAppIcon />;
